const COLLECTION_CATEGORIES = new Set([
  "VizLink",
  "NonVizLink",
  "NonVizLink Recovery",
  "Deposited NonVizLink",
  "Deposited NonVizLink Recovery",
]);

const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i;
const FILE_EXTENSIONS = /\.(pdf|doc|docx|xls|xlsx|csv|txt)(\?.*)?$/i;
const FILE_KEY_PATTERN = /(image|photo|file|attachment|slip|receipt|proof)/i;

export const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return `Rs. ${Math.abs(amount).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

export const getScrV2VarianceLabel = (variance) => {
  const amount = Number(variance || 0);
  if (amount > 0) {
    return "Short";
  }
  if (amount < 0) {
    return "Excess";
  }
  return "Balanced";
};

export const titleize = (key) =>
  (key || "")
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());

export const extractDynamicValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (typeof value === "object") {
    if (Object.prototype.hasOwnProperty.call(value, "value")) {
      return value.value;
    }
    return JSON.stringify(value);
  }

  return String(value);
};

export const isImagePath = (value) => {
  const text = String(extractDynamicValue(value) || "").trim();
  return text.length > 0 && IMAGE_EXTENSIONS.test(text);
};

export const isFilePath = (value, key = "") => {
  const text = String(extractDynamicValue(value) || "").trim();
  if (!text) {
    return false;
  }

  return (
    isImagePath(text) ||
    FILE_EXTENSIONS.test(text) ||
    /^https?:\/\//i.test(text) ||
    (text.includes("/") && FILE_KEY_PATTERN.test(key))
  );
};

export const formatDynamicDisplayValue = (value) => {
  const extracted = extractDynamicValue(value);
  if (extracted === null || extracted === undefined || extracted === "") {
    return "-";
  }

  return String(extracted);
};

const createInvoiceRow = (tx) => ({
  key: tx.shopOrderId || tx.invoiceNo || tx.id,
  shopOrderId: tx.shopOrderId,
  vizShopCode: tx.vizShopCode || "",
  shopName: tx.shopName || "",
  invoiceNo: tx.invoiceNo || "",
  invoiceDate: tx.invoiceDate,
  invoiceAmount: Number(tx.invoiceAmount || 0),
  receivedAmount: 0,
  dueDate: tx.dueDate,
  paymentTypes: [],
  transactions: [],
  isCredit: false,
});

const addTransactionToRow = (row, tx) => {
  row.transactions.push(tx);

  if (Number(tx.invoiceAmount || 0) > 0) {
    row.invoiceAmount = Number(tx.invoiceAmount);
  }

  if (tx.dueDate) {
    row.dueDate = tx.dueDate;
  }

  if (COLLECTION_CATEGORIES.has(tx.transactionCategory) && !tx.isReversed) {
    row.receivedAmount += Number(tx.amount || 0);
    const paymentType = tx.paymentTypeMethod || tx.transactionCategory;
    if (paymentType && !row.paymentTypes.includes(paymentType)) {
      row.paymentTypes.push(paymentType);
    }
  }
};

export const groupTransactionsByInvoice = (transactions) => {
  const map = new Map();

  (transactions || []).forEach((tx) => {
    const key = tx.shopOrderId || tx.invoiceNo || tx.id;
    if (!map.has(key)) {
      map.set(key, createInvoiceRow(tx));
    }
    addTransactionToRow(map.get(key), tx);
  });

  return Array.from(map.values()).sort((a, b) =>
    (a.invoiceNo || "").localeCompare(b.invoiceNo || "")
  );
};

export const buildNormalInvoiceRows = (report) => {
  // SCR Complete Detail — single Invoices list (participating invoices only).
  // Collection (VizLink / NonVizLink / Recovery), adjustments, and credit-marked invoices.
  // Each invoice appears once; credit is a row attribute (isCredit), not a separate tab.
  // Reversal-only activity does not seed membership (reversals are not Linked participation).
  const creditRows = buildCreditInvoiceRows(report);
  const creditByShopOrder = new Map(creditRows.map((row) => [row.shopOrderId, row]));

  const membershipTransactions = [
    ...(report?.vizLinkTransactions || []),
    ...(report?.nonVizLinkTransactions || []),
    ...(report?.invoiceAdjustments || []),
  ].filter((tx) => {
    const shopOrderId = tx.shopOrderId;
    return shopOrderId !== null && shopOrderId !== undefined && shopOrderId !== "";
  });

  const merged = new Map();

  groupTransactionsByInvoice(membershipTransactions).forEach((row) => {
    const credit = creditByShopOrder.get(row.shopOrderId);
    merged.set(row.shopOrderId, {
      ...row,
      isCredit: Boolean(credit),
      receivedAmount: credit ? credit.receivedAmount : row.receivedAmount,
      paymentTypes:
        credit && row.paymentTypes.length === 0 ? credit.paymentTypes : row.paymentTypes,
    });
  });

  creditRows.forEach((creditRow) => {
    if (!merged.has(creditRow.shopOrderId)) {
      merged.set(creditRow.shopOrderId, creditRow);
    }
  });

  return Array.from(merged.values()).sort((a, b) =>
    (a.invoiceNo || "").localeCompare(b.invoiceNo || "")
  );
};

export const buildCreditInvoiceRows = (report) => {
  const collectionsByShopOrder = new Map();

  [
    ...(report?.vizLinkTransactions || []),
    ...(report?.nonVizLinkTransactions || []),
    ...(report?.reversalEntries || []),
  ].forEach((tx) => {
    if (
      tx.shopOrderId &&
      COLLECTION_CATEGORIES.has(tx.transactionCategory) &&
      !tx.isReversed
    ) {
      collectionsByShopOrder.set(
        tx.shopOrderId,
        (collectionsByShopOrder.get(tx.shopOrderId) || 0) + Number(tx.amount || 0)
      );
    }
  });

  return groupTransactionsByInvoice(report?.creditInvoices || []).map((row) => ({
    ...row,
    isCredit: true,
    receivedAmount: collectionsByShopOrder.get(row.shopOrderId) || 0,
    paymentTypes: row.paymentTypes.length > 0 ? row.paymentTypes : ["Credit"],
  }));
};

/** Top-level Invoice Wise Detail accordion sections (presentation only). */
export const INVOICE_ACTIVITY_SECTION_DEFS = [
  {
    id: "adjustments",
    title: "Total Adjustments",
    sourceKey: "invoiceAdjustments",
    totalKey: "totalAdjustmentAmount",
    iconClass: "fas fa-sliders-h",
  },
  {
    id: "credit",
    title: "Total Credit",
    sourceKey: "creditInvoices",
    totalKey: "totalCreditAmount",
    iconClass: "fas fa-credit-card",
  },
  {
    id: "nonVizLink",
    title: "NonVizLink Transactions",
    sourceKey: "nonVizLinkTransactions",
    totalKey: "totalNonVizLinkCollectionAmount",
    iconClass: "fas fa-money-bill-wave",
  },
  {
    id: "vizLink",
    title: "VizLink Transactions",
    sourceKey: "vizLinkTransactions",
    totalKey: "totalVizLinkCollectionAmount",
    iconClass: "fas fa-link",
  },
];

export const collectShopOrderIdsFromTransactions = (transactions) => {
  const ids = new Set();
  (transactions || []).forEach((tx) => {
    if (tx?.shopOrderId !== null && tx?.shopOrderId !== undefined && tx?.shopOrderId !== "") {
      ids.add(String(tx.shopOrderId));
    }
  });
  return ids;
};

const ADJUSTMENT_TYPE_PREFIX = /^Adjustment (?:Addition|Deduction) - /i;

export const formatAdjustmentTypeLabel = (paymentTypeMethod) => {
  const text = String(paymentTypeMethod || "").trim();
  if (!text) {
    return null;
  }

  const stripped = text.replace(ADJUSTMENT_TYPE_PREFIX, "").trim();
  return stripped || text;
};

const collectUniqueLabels = (transactions, shopOrderId, mapLabel) => {
  const labels = new Set();

  (transactions || []).forEach((tx) => {
    if (String(tx.shopOrderId) !== String(shopOrderId)) {
      return;
    }

    const label = mapLabel(tx.paymentTypeMethod);
    if (label) {
      labels.add(label);
    }
  });

  if (labels.size === 0) {
    return null;
  }

  return [...labels].sort().join(", ");
};

/**
 * Section-scoped activity badge for Invoice No. column (presentation only).
 * Credit section intentionally returns null.
 */
export const resolveSectionActivityBadge = (sectionId, shopOrderId, sourceTransactions) => {
  if (sectionId === "credit") {
    return null;
  }

  if (sectionId === "adjustments") {
    return collectUniqueLabels(sourceTransactions, shopOrderId, formatAdjustmentTypeLabel);
  }

  if (sectionId === "nonVizLink" || sectionId === "vizLink") {
    return collectUniqueLabels(sourceTransactions, shopOrderId, (paymentTypeMethod) => {
      const label = String(paymentTypeMethod || "").trim();
      return label || null;
    });
  }

  return null;
};

/**
 * Group existing invoice rows into activity accordion sections.
 * Membership = ShopOrderId present in the existing GetDetailedReport activity array.
 * Same invoice may appear in multiple sections; once per section.
 * Recovery/Reversal are intentionally not top-level sections.
 */
export const buildInvoiceActivitySections = (report) => {
  const allRows = buildNormalInvoiceRows(report);
  const rowsByShopOrderId = new Map(
    allRows
      .filter((row) => row.shopOrderId !== null && row.shopOrderId !== undefined && row.shopOrderId !== "")
      .map((row) => [String(row.shopOrderId), row])
  );
  const totals = report?.totals || {};

  return INVOICE_ACTIVITY_SECTION_DEFS.map((def) => {
    const source = report?.[def.sourceKey] || [];
    const shopOrderIds = collectShopOrderIdsFromTransactions(source);
    const rows = [];

    shopOrderIds.forEach((shopOrderId) => {
      const row = rowsByShopOrderId.get(shopOrderId);
      if (row) {
        rows.push({
          ...row,
          key: `${shopOrderId}-${def.id}`,
          sectionId: def.id,
          sectionActivityBadge: resolveSectionActivityBadge(def.id, shopOrderId, source),
        });
      }
    });

    rows.sort((a, b) => (a.invoiceNo || "").localeCompare(b.invoiceNo || ""));

    return {
      id: def.id,
      title: def.title,
      iconClass: def.iconClass,
      rows,
      invoiceCount: rows.length,
      totalAmount: Number(totals[def.totalKey] || 0),
    };
  }).filter((section) => section.invoiceCount > 0);
};

export const filterInvoiceRows = (rows, searchText) => {
  const query = (searchText || "").trim().toLowerCase();
  if (!query) {
    return rows;
  }

  return rows.filter(
    (row) =>
      row.shopName?.toLowerCase().includes(query) ||
      row.invoiceNo?.toLowerCase().includes(query) ||
      row.vizShopCode?.toLowerCase().includes(query)
  );
};

export const PAGE_SIZE_OPTIONS = [10, 25, 50,100,500];

export const paginateRows = (rows, page, pageSize) => {
  const safePageSize = Math.max(1, pageSize);
  const safePage = Math.max(1, page);
  const start = (safePage - 1) * safePageSize;
  return rows.slice(start, start + safePageSize);
};

export const getTotalPages = (totalRows, pageSize) =>
  Math.max(1, Math.ceil(Math.max(0, totalRows) / Math.max(1, pageSize)));

export const buildVisiblePages = (currentPage, totalPages, maxVisible = 5) => {
  if (totalPages <= 1) {
    return [1];
  }

  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = start + maxVisible - 1;

  if (end > totalPages) {
    end = totalPages;
    start = Math.max(1, end - maxVisible + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};

export const clampPage = (page, totalPages) =>
  Math.min(Math.max(1, page), Math.max(1, totalPages));

export const getReportHighlights = (report) => {
  const totals = report?.totals || {};
  const totalDue = Math.max(
    0,
    Number(totals.totalInvoicesAmount || 0) -
      Number(totals.totalCreditAmount || 0) +
      Number(totals.totalRecoveryAmount || 0)
  );
  const totalReceived =
    Number(totals.totalVizLinkCollectionAmount || 0) +
    Number(totals.totalNonVizLinkCollectionAmount || 0) +
    Number(totals.totalRecoveryAmount || 0);
  const variance = Number(totals.varianceAmount || 0);
  const collectionRate =
    totalDue > 0 ? Math.round((totalReceived / totalDue) * 1000) / 10 : totalReceived > 0 ? 100 : 0;

  return {
    totalDue,
    totalReceived,
    variance,
    collectionRate,
    totalInvoices: Number(totals.totalInvoices || 0),
    varianceLabel: getScrV2VarianceLabel(variance),
    varianceBg: variance > 0 ? "bg-danger" : variance < 0 ? "bg-success" : "bg-primary",
  };
};

export const hasDisplayValue = (value) => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  return true;
};

export const getTransactionFieldRows = (transaction) => {
  // Payment type, amount, and category are shown in the transaction card header.
  const rows = [
    { label: "Bank Name", value: transaction.bankName },
    { label: "Cheque No", value: transaction.chequeNo },
    { label: "Cheque Date", value: transaction.chequeDate, isDate: true },
    { label: "Payer Name", value: transaction.payerName },
    { label: "Transaction Detail", value: transaction.transactionDetail },
    { label: "Comments", value: transaction.comments, fullWidth: true, isComment: true },
  ].filter((row) => hasDisplayValue(row.value));

  if (transaction.isReversed && hasDisplayValue(transaction.reversalDate)) {
    rows.push({
      label: "Reversal Date",
      value: transaction.reversalDate,
      isDateTime: true,
    });
  }

  if (transaction.filePath) {
    rows.push({
      label: "Attachment",
      value: transaction.filePath,
      isFile: true,
      isImage: isImagePath(transaction.filePath),
    });
  }

  const dynamicData = transaction.dynamicData || {};
  Object.keys(dynamicData).forEach((key) => {
    const value = dynamicData[key];
    rows.push({
      label: titleize(key),
      value: extractDynamicValue(value),
      isFile: isFilePath(value, key),
      isImage: isImagePath(value),
      fieldKey: key,
    });
  });

  return rows;
};
